import { useState } from 'react'
import { Box, Grid, Paper, Typography, Stack, Button, Divider, Snackbar, Alert, Select, MenuItem, Slider, FormControlLabel, Switch } from '@mui/material'
import ImageIcon from '@mui/icons-material/Image'
import LayersIcon from '@mui/icons-material/Layers'
import { useTheme } from '@mui/material/styles'
import { useAuth } from '../contexts/AuthContext'
import { brandingApi, BrandingKind } from '../api/branding'
import { authApi } from '../api/auth'

type WMPos = 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

export default function BrandingPage() {
  const muiTheme = useTheme()
  const dark = muiTheme.palette.mode === 'dark'
  const { getEmpresaId } = useAuth()
  const empresaId = getEmpresaId()

  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [wmPreview, setWmPreview] = useState<string | null>(null)
  const [logoKey, setLogoKey] = useState<string | null>(null)
  const [wmKey, setWmKey] = useState<string | null>(null)
  const [wmEnabled, setWmEnabled] = useState(true)
  const [wmOpacity, setWmOpacity] = useState(0.06)
  const [wmScale, setWmScale] = useState(1)
  const [wmPosition, setWmPosition] = useState<WMPos>('center')
  const [busy, setBusy] = useState(false)
  const [snack, setSnack] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)

  const controlText = dark ? '#E2E8F0' : '#0B1220'
  const controlBorder = dark ? '#E2E8F0' : 'rgba(0,0,0,0.23)'
  const paper = dark ? '#1E293B' : '#FFFFFF'

  const uploadFile = async (file: File, kind: BrandingKind) => {
    setBusy(true)
    try {
      const init = await brandingApi.uploadInit(kind, file.name, file.type, empresaId)
      await fetch(init.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
          'x-amz-server-side-encryption': 'AES256',
          'Cache-Control': 'public, max-age=31536000, immutable',
        },
        body: file
      })
      const norm = await brandingApi.normalize(kind, init.file_key, empresaId)
      if (kind === 'logo') { setLogoPreview(norm.preview_url); setLogoKey(init.file_key) }
      if (kind === 'watermark') { setWmPreview(norm.preview_url); setWmKey(init.file_key) }
      setSnack({ type: 'success', msg: 'Upload concluído' })
    } catch (e: any) {
      const url = URL.createObjectURL(file)
      if (kind === 'logo') setLogoPreview(url)
      if (kind === 'watermark') setWmPreview(url)
      setSnack({ type: 'error', msg: 'Falha na API. Exibindo preview local.' })
    } finally {
      setBusy(false)
    }
  }

  const approve = async (kind: BrandingKind) => {
    setBusy(true)
    try {
      const fileKey = kind === 'logo' ? logoKey : wmKey
      if (!fileKey) throw new Error('Arquivo não enviado')
      const res = await brandingApi.approve(kind, fileKey, empresaId)
      if (kind === 'logo') {
        const url = res?.url || logoPreview || null
        if (empresaId && url) {
          await authApi.updatePreferences(empresaId, { logoUrl: url })
        }
        try {
          const ctxStr = localStorage.getItem('user_context')
          if (ctxStr && url) {
            const ctx = JSON.parse(ctxStr)
            ctx.preferencias = ctx.preferencias || {}
            ctx.preferencias.ui = ctx.preferencias.ui || {}
            ctx.preferencias.ui.logoUrl = url
            localStorage.setItem('user_context', JSON.stringify(ctx))
          }
        } catch {}
      }
      if (kind === 'watermark') {
        await authApi.updatePreferences(empresaId!, { watermark: { enabled: wmEnabled, opacity: wmOpacity, scale: wmScale, position: wmPosition } })
      }
      setSnack({ type: 'success', msg: 'Aprovado' })
    } catch {
      setSnack({ type: 'error', msg: 'Erro ao aprovar' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Branding</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Atualize logo do header e watermark de fundo.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo (Header)</Typography>
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <ImageIcon sx={{ color: controlText }} />
                <Typography variant="body2" sx={{ color: controlText }}>Pré-visualização</Typography>
              </Stack>
              <Box sx={{ width: 180, height: 48, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', color: '#0B1220', overflow: 'hidden' }}>
                {logoPreview ? <img src={logoPreview} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%' }} /> : <Typography variant="caption" sx={{ fontWeight: 600 }}>LOGO</Typography>}
              </Box>
            </Box>
            <Stack direction="row" gap={1.5} sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                component="label"
                sx={{ color: controlText, borderColor: controlBorder, '&:hover': { borderColor: dark ? '#F1F5F9' : controlBorder, backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)' } }}
              >
                Upload
                <input hidden type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, 'logo') }} />
              </Button>
              <Button size="small" variant="contained" disabled={busy || !logoPreview} onClick={() => approve('logo')}>Aprovar</Button>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Watermark (Fundo)</Typography>
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                <LayersIcon sx={{ color: controlText }} />
                <Typography variant="body2" sx={{ color: controlText }}>Pré-visualização</Typography>
              </Stack>
              <Box
                sx={{
                  height: 180,
                  borderRadius: 1,
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: paper,
                  backgroundImage: wmPreview ? `url(${wmPreview})` : 'none',
                  backgroundSize: `${wmScale * 60}%`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition:
                    wmPosition === 'center' ? 'center' :
                      wmPosition === 'top-left' ? 'left 16px top 16px' :
                        wmPosition === 'top-right' ? 'right 16px top 16px' :
                          wmPosition === 'bottom-left' ? 'left 16px bottom 16px' :
                            'right 16px bottom 16px',
                  opacity: wmEnabled ? wmOpacity : 0,
                }}
              />
            </Box>
            <Stack direction="row" gap={1.5} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              <FormControlLabel control={<Switch checked={wmEnabled} onChange={(e) => setWmEnabled(e.target.checked)} />} label="Ativo" />
              <Box sx={{ width: 200, px: 1 }}>
                <Typography variant="caption">Opacidade</Typography>
                <Slider size="small" value={wmOpacity} min={0} max={0.3} step={0.01} onChange={(_, v) => setWmOpacity(v as number)} />
              </Box>
              <Box sx={{ width: 200, px: 1 }}>
                <Typography variant="caption">Escala</Typography>
                <Slider size="small" value={wmScale} min={0.5} max={1.5} step={0.05} onChange={(_, v) => setWmScale(v as number)} />
              </Box>
              <Box sx={{ minWidth: 160 }}>
                <Typography variant="caption">Posição</Typography>
                <Select size="small" fullWidth value={wmPosition} onChange={(e) => setWmPosition(e.target.value as WMPos)}>
                  <MenuItem value="center">Centro</MenuItem>
                  <MenuItem value="top-left">Topo Esquerda</MenuItem>
                  <MenuItem value="top-right">Topo Direita</MenuItem>
                  <MenuItem value="bottom-left">Base Esquerda</MenuItem>
                  <MenuItem value="bottom-right">Base Direita</MenuItem>
                </Select>
              </Box>
              <Button
                size="small"
                variant="outlined"
                component="label"
                disabled={busy}
                sx={{ color: controlText, borderColor: controlBorder, '&:hover': { borderColor: dark ? '#F1F5F9' : controlBorder, backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)' } }}
              >
                Upload
                <input hidden type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, 'watermark') }} />
              </Button>
              <Button size="small" variant="contained" disabled={busy || !wmPreview} onClick={() => approve('watermark')}>Aprovar</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.type} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  )
}
